import Button from "@marginly/ui/components/button";
import T from "@marginly/ui/components/typography";
import { cssVar } from "@marginly/ui/util/style";
import cn from "classnames";
import NextLink from "next/link";
import { useParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAccount } from "wagmi";

import { getTokenSymbol } from "@/app/util";
import { TradeBlock } from "@/components/common/trading-block";
import {
  CHART_BACK_GREEN,
  CHART_BACK_RED,
  TEXT_GREEN,
  TEXT_RED,
} from "@/constants/common";
import { useWeb3Context } from "@/contexts/web3";
import { PositionType } from "@/contracts/transform/position";
import { useMarginlyApi } from "@/hooks/api";
import { usePoolData, usePoolParams } from "@/hooks/position";
import { getPriceAggregates, toChartCandleSeries } from "@/util/common";
import { formatNum, formatTvl } from "@/util/format";
import { formatCoin } from "@/util/format-coin";

import { usePricePoints } from "../../../hooks/use-price-points";
import { checkIsWatch } from "../../../util/position-helpers";
import { SimplePriceSVGGraph } from "../../common/simple-graph";
import Space from "../../common/space";
import { Badge, PositionBadge, TokenIcon, TVLBar } from "./styled";

type Props = {
  address?: `0x${string}`;
  position?: PositionType;
  internalChainId: number;
};

const TVL_GOAL = 200_000;

export const Pool = ({ address, position, internalChainId }: Props) => {
  const { base, basePrice, quote, awaiting, uniswapAddress, refetch } =
    usePoolData(address);

  const ref = useRef<HTMLDivElement>(null);
  const [parentWidth, setParentWidth] = useState(0);

  const { getPoolByAddress } = useWeb3Context();
  const isPendle = getPoolByAddress(address)?.pool.isPendle;

  const points = usePricePoints({
    internalChainId,
    isPendle,
    onUpdate: refetch,
    poolAddress: uniswapAddress!,
  });

  const candles = useMemo(() => points?.map(toChartCandleSeries), [points]);
  const price = getPriceAggregates(candles);

  useEffect(() => {
    const handleResize = () => {
      window.requestAnimationFrame(() => {
        const rect =
          typeof ref !== "function"
            ? ref?.current?.getBoundingClientRect()
            : undefined;
        if (rect) {
          setParentWidth(rect?.width || 0);
        }
      });
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("load", handleResize);

    return () => {
      window.removeEventListener("load", handleResize);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const { chain } = useParams();

  const tvl = useMarginlyApi({
    endpoint: "poolTvl",
    args: [internalChainId, address],
  });

  const getPercents = (tvl: number | undefined) => {
    if (!tvl) return 0;

    return (100 * tvl) / TVL_GOAL;
  };

  const account = useAccount();

  const maxLeverage = usePoolParams(address).maxLeverage;
  const leverageBadgeValue = maxLeverage ?? 20;

  return (
    <TradeBlock
      className={cn({ up: price?.up, awaiting }, "graph-parent")}
      ref={ref}
    >
      {awaiting ? (
        <>
          <div>
            <div className="trade__heading">
              {base?.symbol && <TokenIcon token={base.symbol} />}
              <p>
                {getTokenSymbol(base?.symbol)} / {getTokenSymbol(quote?.symbol)}
              </p>
            </div>
            <Space height={10} heightMobile={10} />
            <TVLBar>
              <div
                className="filled"
                style={{ width: getPercents(tvl?.data?.tvlUsd) }}
              />
            </TVLBar>
            <Space height={4} heightMobile={4} />
            <T caption secondary>
              Available at ${formatTvl(TVL_GOAL)} TVL
            </T>
            <Space height={12} heightMobile={12} />
          </div>
          <div className="awaiting__buttons">
            <NextLink
              href={
                account.isConnected
                  ? `/${chain}/lend-info/base:${address}`
                  : `/${chain}/deposit/base:${address}`
              }
            >
              <Button md elevated>
                <T action>Lend {getTokenSymbol(base?.symbol)}</T>
              </Button>
            </NextLink>
            <NextLink
              href={
                account.isConnected
                  ? `/${chain}/lend-info/quote:${address}`
                  : `/${chain}/deposit/quote:${address}`
              }
            >
              <Button md elevated>
                <T action>Lend {getTokenSymbol(quote?.symbol)}</T>
              </Button>
            </NextLink>
          </div>
        </>
      ) : (
        <>
          <div className="trade__heading">
            {base?.symbol && <TokenIcon token={base.symbol} />}
            <p>{getTokenSymbol(base?.symbol)}</p>
          </div>
          {!awaiting && checkIsWatch(position) ? (
            <PositionBadge md className={cn({ up: price?.up })}>
              {position === PositionType.Long ? "Long" : "Short"}
            </PositionBadge>
          ) : (
            <Badge md>
              {leverageBadgeValue}
              &times;
            </Badge>
          )}

          <SimplePriceSVGGraph
            className="graph"
            candles={candles}
            width={parentWidth}
            height={64}
            color={
              awaiting
                ? cssVar("--text-tertiary")
                : price?.up
                ? CHART_BACK_GREEN
                : CHART_BACK_RED
            }
          />
          <div className="trade__bottom">
            {formatCoin(basePrice)}
            <br />
            {getTokenSymbol(quote?.symbol)}
            <div
              className="trade__percents"
              style={
                awaiting
                  ? undefined
                  : { color: price?.up ? TEXT_GREEN : TEXT_RED }
              }
            >
              {formatNum(price?.percent, {
                prefix: price && price.diff?.gt(0) ? "+" : "",
                postfix: "%",
                dp: 2,
              })}
            </div>
          </div>
        </>
      )}
    </TradeBlock>
  );
};
